import { Nullable } from 'global';
import { v4 as uuidv4 } from 'uuid';
import { formatStringToNumber } from 'utils/formatters';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import { DiscountType } from '@trnsact/trnsact-shared-types';
import { UpdateInvoiceVariables } from 'modules/invoice/api/types';
import { ChargeAttachType, ChargeParentKind, Charge, Equipment } from 'modules/equipment/types';

import { equipmentCalc, ChargeListingService } from '../lib';
import { calcFees, calcTaxes, calculateTotal, chargeCalc, chargeTypesByLabels, newChargeRow } from '../../equipment';
import {
  Quote,
  QuoteInput,
  EquipmentLike,
  EquipmentSyncDTO,
  QuoteLineItemType,
  QuoteEquipmentEntry,
  QuoteChargeListingEntity,
  QuoteEquipmentWithCharge,
} from '../types';

class QuoteMapper {
  private readonly chargeListingService = new ChargeListingService();

  public toListingRow(equipment: Record<string, any>, quote: Nullable<Quote>): QuoteChargeListingEntity {
    const priceFromQuote = (quote?.equipment ?? []).find(
      quoteEquipment => quoteEquipment.equipmentId === equipment.equipmentId
    )?.price;

    return {
      charge: null,
      type: QuoteLineItemType.Equipment,
      equipment: {
        ...equipment,
        price: priceFromQuote
          ? {
              ...priceFromQuote,
              wholesalePrice: equipment.wholesaleCost ?? priceFromQuote.wholesalePrice,
              retailPrice: equipment.retailPrice ?? priceFromQuote.retailPrice,
            }
          : {
              ...newChargeRow,
              wholesalePrice: equipment.wholesaleCost ?? 0,
              retailPrice: equipment.retailPrice ?? 0,
            },
        attachments: (equipment.attachments ?? []).map((attachment: any) => ({
          ...attachment,
          price: {
            ...newChargeRow,
            retailPrice: attachment.retailPrice ?? 0,
            discount: attachment.discountValue ?? 0,
            wholesalePrice: formatStringToNumber(attachment.unitCost) ?? 0,
            discountType: attachment.discountType ?? DiscountType.Flat,
          },
        })),
      },
    };
  }

  public toListingRows(voId: string, list: Equipment[], quote: Nullable<Quote>): QuoteChargeListingEntity[] {
    if (!Array.isArray(list) || !list.length) return [];

    const equipmentRows = list.map(entity => this.toListingRow(entity, quote));
    const parentKindById = this.buildParentKindLookup(equipmentRows);
    const feeRows = (quote?.fees ?? []).map(fee => this.toFeeListingRow(fee, voId, parentKindById));

    return this.chargeListingService.buildBuckets([...equipmentRows, ...feeRows], voId).flatMap(bucket => bucket.data);
  }

  private toFeeListingRow(
    fee: Quote['fees'][number],
    voId: string,
    parentKindById: Map<string, ChargeParentKind>
  ): QuoteChargeListingEntity {
    const parentId = fee.parentId ?? null;
    const parentType: ChargeParentKind | undefined = parentId ? (parentKindById.get(parentId) ?? 'charge') : undefined;

    return {
      type: QuoteLineItemType.Invoice,
      equipment: null,
      charge: {
        type: fee.type,
        label: fee.description,
        id: fee.id ?? uuidv4(),
        parentId,
        parentType,
        price: fee?.price ?? newChargeRow,
        financeType: chargeTypesByLabels[fee.description],
        amount: fee?.amount,
        amountType: fee?.amountType,
        isTaxable: fee?.isTaxable ?? undefined,
        customerVisibility: fee?.customerVisibility ?? undefined,
        isFromCommonRules: fee?.isFromCommonRules ?? false,
        groupBucket: {
          id: fee.equipmentId ?? voId,
          type: fee.equipmentId ? ChargeAttachType.Equipment : ChargeAttachType.Invoice,
        },
      },
    };
  }

  private buildParentKindLookup(equipmentRows: QuoteChargeListingEntity[]): Map<string, ChargeParentKind> {
    const map = new Map<string, ChargeParentKind>();

    equipmentRows.forEach(row => {
      if (row.type !== QuoteLineItemType.Equipment || !row.equipment) return;
      map.set(row.equipment.equipmentId, 'equipment');
    });

    return map;
  }

  public toQuoteVariables(input: QuoteChargeListingEntity[]): QuoteInput {
    const equipment = input
      .filter(row => row.type === QuoteLineItemType.Equipment)
      .map(row => this.toQuoteEquipmentEntry(row.equipment!));

    const fees = input
      .filter(row => row.type === QuoteLineItemType.Invoice)
      .map(row => this.toQuoteFeeEntry(row.charge!));

    return removeGraphQLMEtaFields({ ...this.emptyQuoteInput(), fees, equipment });
  }

  public toEquipmentQuoteVariables(equipment: EquipmentLike, quote: Nullable<Quote>, charges: Charge[]): QuoteInput {
    const equipmentEntry: QuoteEquipmentEntry = {
      attributes: [],
      externalId: '',
      cost: equipment.retailPrice,
      equipmentId: equipment.equipmentId,
      stockNumber: equipment.stockId ?? '',
      vin: equipment.serialNumberVin ?? '',
      price: equipment.price ?? { ...newChargeRow, retailPrice: equipment.retailPrice ?? 0 },
    };

    if (!quote) {
      return {
        ...this.emptyQuoteInput(),
        fees: charges.map(charge => this.toQuoteFeeForEquipment(charge, equipment.equipmentId)),
        equipment: [equipmentEntry],
      };
    }

    const otherFees = quote.fees.filter(fee => fee.equipmentId !== equipment.equipmentId);
    const newFees = charges.map(charge => this.toQuoteFeeForEquipment(charge, equipment.equipmentId));

    return {
      ...quote,
      fees: [...otherFees, ...newFees],
      equipment: quote.equipment.map(quoteEquipment =>
        equipment.equipmentId === quoteEquipment.equipmentId ? equipmentEntry : quoteEquipment
      ),
    };
  }

  private toQuoteEquipmentEntry(equipment: QuoteEquipmentWithCharge): QuoteEquipmentEntry {
    return {
      attributes: [],
      externalId: '',
      cost: equipment.price.retailPrice,
      equipmentId: equipment.equipmentId,
      stockNumber: equipment.stockId ?? '',
      vin: equipment.serialNumberVin ?? '',
      price: equipment.price ?? newChargeRow,
    };
  }

  private toQuoteFeeEntry(charge: Charge): QuoteInput['fees'][number] {
    const retailPrice = chargeCalc.calcRetailPrice(charge);

    return {
      id: charge.id,
      type: charge.type,
      amount: retailPrice,
      description: charge.label,
      parentId: charge.parentId,
      isTaxable: charge.isTaxable,
      parentType: charge.parentType,
      amountType: charge.amountType ?? 'flat',
      price: { ...charge.price, retailPrice },
      customerVisibility: charge.customerVisibility,
      equipmentId: charge.groupBucket.type === ChargeAttachType.Equipment ? charge.groupBucket.id : null,
    };
  }

  private toQuoteFeeForEquipment(charge: Charge, equipmentId: string): QuoteInput['fees'][number] {
    return {
      equipmentId,
      type: charge.type,
      price: charge.price,
      amountType: 'flat',
      description: charge.label,
      amount: calculateTotal(charge.price),
    };
  }

  private emptyQuoteInput(): QuoteInput {
    return {
      term: null,
      fees: [],
      equipment: [],
      downPayment: null,
      interestRate: null,
      paymentAmount: null,
      contractStartDate: null,
      firstPaymentDueDate: null,
      rebates: [],
      attributes: [],
      cashBackAmounts: [],
      equipmentTradeIns: [],
      aftermarketProducts: [],
    };
  }

  public toInvoiceDTO({ equipment, fees }: QuoteInput): UpdateInvoiceVariables['fieldsObj'] {
    const equipmentCost = equipment.reduce((cost, entry) => cost + (entry.price?.retailPrice ?? 0), 0);

    const salesTaxOnInvoice = fees.reduce((total, fee) => total + calcTaxes(fee as unknown as Charge), 0);
    const freightLaborInstallation = fees.reduce((total, fee) => total + calcFees(fee as unknown as Charge), 0);

    return { equipmentCost, salesTaxOnInvoice, freightLaborInstallation };
  }

  public toEquipmentSyncDTO(equipmentForUpdate: QuoteEquipmentWithCharge) {
    const { price } = equipmentForUpdate;

    const result: EquipmentSyncDTO = {
      year: equipmentForUpdate.year,
      make: equipmentForUpdate.make,
      model: equipmentForUpdate.model,
      mileage: equipmentForUpdate.mileage,
      stockId: equipmentForUpdate.stockId,
      quantity: equipmentForUpdate.quantity,
      truckClass: equipmentForUpdate.class,
      newUsed: equipmentForUpdate.condition,
      wholesaleCost: price.wholesalePrice ?? 0,
      serialNumberVin: equipmentForUpdate?.vin?.trim(),
      totalWholesaleCost: equipmentForUpdate.wholeSaleCost,
      additionalDescription: equipmentForUpdate.description,
      retailPrice: equipmentCalc.calcRetailPrice(equipmentForUpdate),
    };

    return {
      ...result,
      discountValue: price.discount ?? 0,
      discountType: price.discountType ?? DiscountType.Flat,
    };
  }

  public hasEquipmentSyncChanges(
    equipmentForUpdate: QuoteEquipmentWithCharge,
    oldEquipment: Record<string, any>
  ): boolean {
    const { price } = equipmentForUpdate;

    return [
      oldEquipment.retailPrice !== price.retailPrice,
      oldEquipment.discountValue !== price.discount,
      oldEquipment.wholesaleCost !== price.wholesalePrice,
      oldEquipment.discountType !== price.discountType,
    ].some(Boolean);
  }
}

export const quoteMapper = new QuoteMapper();
